import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Shield, TrendingUp, Calendar, Clock, CheckCircle, AlertCircle,
  MapPin, Car, User, Hash, Phone, ChevronRight,
  RefreshCw, Star, Activity, Wallet, IndianRupee, XCircle,
  ArrowUpRight, ArrowDownRight, Info, Timer, BanknoteIcon
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── tiny helpers ─────────────────────────────────────────── */
const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDate  = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const daysUntil = (isoStr) => {
  if (!isoStr) return null;
  const diff = Math.ceil((new Date(isoStr) - Date.now()) / 86400000);
  return diff;
};

const statusColor = (s) => {
  if (s === 'submitted' || s === 'verified') return 'bg-green-100 text-green-700 border-green-200';
  if (s === 'pending')     return 'bg-yellow-100 text-yellow-700 border-yellow-200';
  if (s === 'in_progress') return 'bg-blue-100 text-blue-700 border-blue-200';
  return 'bg-gray-100 text-gray-600 border-gray-200';
};

/* ─── Payout timeline component ────────────────────────────── */
const PayoutTimeline = ({ transactions, cycleLabel }) => {
  const grouped = {};
  transactions.forEach(t => {
    const month = t.created_at?.slice(0, 7) || 'Unknown';
    if (!grouped[month]) grouped[month] = [];
    grouped[month].push(t);
  });

  return (
    <div className="space-y-4">
      {Object.entries(grouped).slice(0, 4).map(([month, txns]) => (
        <div key={month}>
          <p className="text-xs font-semibold text-gray-400 uppercase mb-2">
            {new Date(month + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
          </p>
          <div className="space-y-2">
            {txns.map((t, i) => {
              const days = daysUntil(t.payout_due_at);
              const isOverdue = days !== null && days < 0;
              const isPaid = t.payout_status === 'paid';
              return (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl border bg-white hover:bg-gray-50 transition">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isPaid ? 'bg-green-100' : isOverdue ? 'bg-red-100' : 'bg-blue-50'}`}>
                      {isPaid
                        ? <CheckCircle className="h-4 w-4 text-green-600" />
                        : isOverdue
                          ? <AlertCircle className="h-4 w-4 text-red-500" />
                          : <Timer className="h-4 w-4 text-blue-500" />
                      }
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">Vehicle Inspection</p>
                      <p className="text-xs text-gray-400">{fmtDate(t.created_at)} · {t.city || '—'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-green-600">{fmtMoney(t.amount)}</p>
                    {isPaid
                      ? <p className="text-xs text-green-500 font-medium">Paid ✓</p>
                      : <p className={`text-xs font-medium ${isOverdue ? 'text-red-500' : 'text-blue-500'}`}>
                          {isOverdue ? `Overdue by ${Math.abs(days)}d` : `Due in ${days}d`}
                        </p>
                    }
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ─── Main component ────────────────────────────────────────── */
const PartnerDashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  const [earnings, setEarnings] = useState(null);
  const [assignedVehicles, setAssignedVehicles] = useState([]);
  const [availability, setAvailability] = useState([]);

  const [schedules, setSchedules] = useState([
    { date: '', from_time: '10:00', to_time: '18:00' },
    { date: '', from_time: '10:00', to_time: '18:00' },
  ]);
  const [savingAvail, setSavingAvail] = useState(false);

  const todayStr = () => new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (!user || user.role !== 'partner') { navigate('/login'); return; }
    fetchAll();
    fetchCities();
  }, [user]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchEarnings(), fetchAssigned(), fetchAvailability()]);
    } finally { setLoading(false); }
  };

  const fetchCities  = async () => { try { const r = await axios.get(`${API}/cities`); setCities(r.data.cities || []); } catch {} };
  const fetchEarnings = async () => {
    try {
      const r = await axios.get(`${API}/partner/earnings`, { headers: { Authorization: `Bearer ${token}` } });
      setEarnings(r.data);
    } catch {}
  };
  const fetchAssigned = async () => {
    try {
      const r = await axios.get(`${API}/partner/assigned-vehicles`, { headers: { Authorization: `Bearer ${token}` } });
      setAssignedVehicles(r.data.vehicles || []);
    } catch {}
  };
  const fetchAvailability = async () => {
    try {
      const r = await axios.get(`${API}/partner/availability`, { headers: { Authorization: `Bearer ${token}` } });
      setAvailability(r.data.availability || []);
    } catch {}
  };

  const saveAvailability = async () => {
    const valid = schedules.filter(s => s.date);
    if (!valid.length) { toast.error('Add at least one date'); return; }
    setSavingAvail(true);
    try {
      await axios.post(`${API}/partner/availability`, { schedules: valid }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Availability saved!');
      fetchAvailability();
      setSchedules([{ date: '', from_time: '10:00', to_time: '18:00' }, { date: '', from_time: '10:00', to_time: '18:00' }]);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSavingAvail(false); }
  };

  const pendingJobs   = assignedVehicles.filter(v => v.assignment_status === 'pending');
  const completedJobs = assignedVehicles.filter(v => ['submitted', 'verified'].includes(v.assignment_status));

  const TABS = [
    { key: 'overview',      label: 'Overview',      icon: Activity },
    { key: 'vehicles',      label: 'Assigned Jobs',  icon: Car },
    { key: 'earnings',      label: 'Earnings',       icon: IndianRupee },
    { key: 'availability',  label: 'Availability',   icon: Calendar },
  ];

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full" />
    </div>
  );

  const cycleLabel = earnings?.payout_cycle_days === 15 ? '15-day' : '30-day';
  const nextPayoutDays = daysUntil(earnings?.next_payout_date ? earnings.next_payout_date + 'T00:00:00' : null);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header cities={cities} selectedCity={selectedCity} setSelectedCity={setSelectedCity} showSearch={false} />

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">

        {/* ── Profile header ── */}
        <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-blue-700 to-indigo-700 rounded-2xl p-6 mb-8 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -translate-y-16 translate-x-16" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-y-10 -translate-x-10" />
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center text-2xl font-bold flex-shrink-0">
              {user?.name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-1">
                <h1 className="text-xl font-bold">{user?.name}</h1>
                <span className="text-xs bg-white/20 px-3 py-1 rounded-full font-semibold">Partner</span>
                {user?.active !== false
                  ? <span className="text-xs bg-green-400/30 border border-green-300/40 px-3 py-1 rounded-full font-semibold text-green-200">● Active</span>
                  : <span className="text-xs bg-red-400/30 border border-red-300/40 px-3 py-1 rounded-full font-semibold text-red-200">● Inactive</span>
                }
                {/* Payout cycle badge */}
                <span className="text-xs bg-amber-400/30 border border-amber-300/40 px-3 py-1 rounded-full font-semibold text-amber-200">
                  {cycleLabel} payout cycle
                </span>
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-blue-200">
                <span className="flex items-center gap-1.5"><Hash className="h-3.5 w-3.5" /><span className="font-mono font-semibold text-white">{user?.partner_uid || 'X7-PAR-XXXXXXXX'}</span></span>
                <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{user?.city || '—'}</span>
                <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{user?.phone || '—'}</span>
                <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />Joined: {fmtDate(user?.created_at)}</span>
              </div>
            </div>
            <button onClick={fetchAll} className="flex items-center gap-2 text-sm text-blue-200 hover:text-white transition bg-white/10 hover:bg-white/20 px-4 py-2 rounded-xl">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
          </div>
        </motion.div>

        {/* ── Next payout banner ── */}
        {earnings && earnings.pending_payout_total > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="mb-6 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <BanknoteIcon className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-amber-800">
                  {fmtMoney(earnings.pending_payout_total)} pending payout
                </p>
                <p className="text-xs text-amber-600">
                  {earnings.next_payout_date
                    ? `Next payout on ${fmtDate(earnings.next_payout_date)} (${nextPayoutDays !== null && nextPayoutDays >= 0 ? `in ${nextPayoutDays} day${nextPayoutDays !== 1 ? 's' : ''}` : 'overdue'})`
                    : 'No payout scheduled yet'
                  } · {cycleLabel} cycle
                </p>
              </div>
            </div>
            <span className="text-xs bg-amber-100 text-amber-700 px-3 py-1.5 rounded-full font-semibold border border-amber-200">
              Payout after {earnings.payout_cycle_days} days of inspection
            </span>
          </motion.div>
        )}

        {/* ── Quick stats ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'All-Time Earned',  value: fmtMoney(earnings?.all_time_earnings), icon: Wallet,      color: 'text-green-600',  bg: 'bg-green-50'  },
            { label: 'This Month',       value: fmtMoney(earnings?.last_30_days),       icon: TrendingUp,  color: 'text-blue-600',   bg: 'bg-blue-50'   },
            { label: 'This Week',        value: fmtMoney(earnings?.last_7_days),         icon: Activity,    color: 'text-purple-600', bg: 'bg-purple-50' },
            { label: "Today",            value: fmtMoney(earnings?.today),               icon: IndianRupee, color: 'text-orange-600', bg: 'bg-orange-50' },
          ].map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}>
              <Card className="border border-gray-200 hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className={`w-9 h-9 ${s.bg} rounded-xl flex items-center justify-center mb-3`}>
                    <s.icon className={`h-4.5 w-4.5 ${s.color}`} />
                  </div>
                  <p className="text-xs text-gray-500 mb-0.5">{s.label}</p>
                  <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* ── Tabs ── */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium flex-shrink-0 transition-all ${activeTab === key ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        {/* ══════════ OVERVIEW TAB ══════════ */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { label: 'Pending Jobs',   value: pendingJobs.length,   color: 'text-yellow-600', bg: 'bg-yellow-50 border-yellow-200', icon: Clock },
                { label: 'Completed Jobs', value: completedJobs.length, color: 'text-green-600',  bg: 'bg-green-50 border-green-200',  icon: CheckCircle },
                { label: 'Total Assigned', value: assignedVehicles.length, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200',  icon: Car },
              ].map((s, i) => (
                <Card key={i} className={`border ${s.bg}`}>
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl ${s.bg} flex items-center justify-center`}>
                      <s.icon className={`h-6 w-6 ${s.color}`} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                      <p className="text-sm text-gray-500">{s.label}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Payout status card */}
            {earnings && (
              <Card className="border border-gray-200">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Wallet className="h-5 w-5 text-blue-600" />Payout Summary</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                      <p className="text-xs text-amber-600 mb-1">Pending Payout</p>
                      <p className="text-xl font-bold text-amber-700">{fmtMoney(earnings.pending_payout_total)}</p>
                      {earnings.next_payout_date && <p className="text-xs text-amber-500 mt-0.5">Due: {fmtDate(earnings.next_payout_date)}</p>}
                    </div>
                    <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                      <p className="text-xs text-green-600 mb-1">Total Paid Out</p>
                      <p className="text-xl font-bold text-green-700">{fmtMoney(earnings.paid_out_total)}</p>
                      <p className="text-xs text-green-500 mt-0.5">Settled</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                      <p className="text-xs text-blue-600 mb-1">Payout Cycle</p>
                      <p className="text-xl font-bold text-blue-700">{earnings.payout_cycle_days} days</p>
                      <p className="text-xs text-blue-500 mt-0.5">After inspection date</p>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <p className="text-xs text-gray-500 flex items-start gap-1.5">
                      <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                      Earnings are based on the vehicle verification base price. GST and platform commission are deducted before payout.
                      Your payout is processed every <strong>{earnings.payout_cycle_days} days</strong> from the date of each completed inspection.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {pendingJobs.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2 text-base"><AlertCircle className="h-5 w-5 text-yellow-500" />Pending Inspections</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {pendingJobs.slice(0, 3).map((v, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">{v.brand_name} {v.model_name}</p>
                        <p className="text-xs text-gray-500">{v.registration_no} • {v.city}</p>
                      </div>
                      <Badge className="bg-yellow-100 text-yellow-700 border border-yellow-200 text-xs">Pending</Badge>
                    </div>
                  ))}
                  {pendingJobs.length > 3 && <p className="text-xs text-gray-400 text-center">+{pendingJobs.length - 3} more</p>}
                </CardContent>
              </Card>
            )}

            {earnings?.transactions?.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><IndianRupee className="h-5 w-5 text-green-600" />Recent Payments</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {earnings.transactions.slice(0, 5).map((t, i) => {
                    const days = daysUntil(t.payout_due_at);
                    const isPaid = t.payout_status === 'paid';
                    return (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                        <div>
                          <p className="text-sm font-medium text-gray-800">Inspection completed</p>
                          <p className="text-xs text-gray-400">{fmtDate(t.created_at)} · {t.city || '—'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-green-600 font-bold text-sm">{fmtMoney(t.amount)}</p>
                          {isPaid
                            ? <p className="text-xs text-green-500">Paid ✓</p>
                            : <p className="text-xs text-blue-500">Due: {fmtDate(t.payout_due_at)}</p>
                          }
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ══════════ ASSIGNED VEHICLES TAB ══════════ */}
        {activeTab === 'vehicles' && (
          <div className="space-y-4">
            {assignedVehicles.length === 0 ? (
              <Card><CardContent className="p-12 text-center">
                <Car className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No vehicles assigned yet</p>
                <p className="text-sm text-gray-400 mt-1">Set your availability to start receiving assignments</p>
              </CardContent></Card>
            ) : assignedVehicles.map((vehicle, i) => (
              <motion.div key={vehicle.id || i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                      {vehicle.images?.[0] && <img src={vehicle.images[0]} alt="vehicle" className="w-full sm:w-28 h-24 object-cover rounded-xl flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h3 className="font-bold text-gray-900">{vehicle.brand_name} {vehicle.model_name}</h3>
                          <Badge className={`text-xs border ${statusColor(vehicle.assignment_status)}`}>{vehicle.assignment_status?.replace('_', ' ')}</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-600 mb-3">
                          <span className="flex items-center gap-1.5"><Hash className="h-3.5 w-3.5 text-gray-400" />{vehicle.registration_no}</span>
                          <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-gray-400" />{vehicle.city}</span>
                          <span className="flex items-center gap-1.5"><Car className="h-3.5 w-3.5 text-gray-400" />{vehicle.vehicle_type}</span>
                          <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-gray-400" />{vehicle.manufacturing_year}</span>
                        </div>
                        {vehicle.assignment_status === 'pending' && (
                          <button onClick={() => navigate(`/partner/inspect/${vehicle.id}`)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition">
                            <Shield className="h-4 w-4" /> Start Inspection <ChevronRight className="h-4 w-4" />
                          </button>
                        )}
                        {['submitted', 'verified'].includes(vehicle.assignment_status) && (
                          <span className="flex items-center gap-2 text-green-600 text-sm font-medium">
                            <CheckCircle className="h-4 w-4" /> Inspection completed
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* ══════════ EARNINGS TAB ══════════ */}
        {activeTab === 'earnings' && (
          <div className="space-y-6">
            {/* Hero earnings card */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0">
                <CardContent className="p-6">
                  <p className="text-green-100 text-sm mb-1">All-Time Earnings</p>
                  <p className="text-4xl font-bold">{fmtMoney(earnings?.all_time_earnings)}</p>
                  <p className="text-green-200 text-xs mt-2">Total received since joining</p>
                </CardContent>
              </Card>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'This Month', value: earnings?.last_30_days, color: 'text-blue-600' },
                  { label: 'This Week',  value: earnings?.last_7_days,  color: 'text-purple-600' },
                  { label: 'Today',      value: earnings?.today,         color: 'text-orange-600' },
                  { label: 'Inspections', value: earnings?.transactions?.length || 0, color: 'text-gray-700', isCount: true },
                ].map((s, i) => (
                  <Card key={i}><CardContent className="p-4">
                    <p className="text-xs text-gray-500 mb-1">{s.label}</p>
                    <p className={`text-xl font-bold ${s.color}`}>{s.isCount ? s.value : fmtMoney(s.value)}</p>
                  </CardContent></Card>
                ))}
              </div>
            </div>

            {/* Payout breakdown */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="border border-amber-200 bg-amber-50">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Timer className="h-5 w-5 text-amber-600" />
                    <p className="font-semibold text-amber-800">Pending Payout</p>
                  </div>
                  <p className="text-3xl font-bold text-amber-700">{fmtMoney(earnings?.pending_payout_total)}</p>
                  {earnings?.next_payout_date && (
                    <p className="text-sm text-amber-600 mt-2">
                      Next payout: <strong>{fmtDate(earnings.next_payout_date)}</strong>
                      {nextPayoutDays !== null && (
                        <span className="ml-1">
                          ({nextPayoutDays >= 0 ? `in ${nextPayoutDays} day${nextPayoutDays !== 1 ? 's' : ''}` : `overdue by ${Math.abs(nextPayoutDays)} days`})
                        </span>
                      )}
                    </p>
                  )}
                  <p className="text-xs text-amber-500 mt-1">Paid {earnings?.payout_cycle_days} days after each inspection</p>
                </CardContent>
              </Card>

              <Card className="border border-green-200 bg-green-50">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <p className="font-semibold text-green-800">Total Paid Out</p>
                  </div>
                  <p className="text-3xl font-bold text-green-700">{fmtMoney(earnings?.paid_out_total)}</p>
                  <p className="text-sm text-green-600 mt-2">Successfully settled</p>
                  <p className="text-xs text-green-500 mt-1">Your {earnings?.payout_cycle_days}-day payout cycle</p>
                </CardContent>
              </Card>
            </div>

            {/* Info notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
              <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-700">
                Your earnings are calculated from the vehicle verification fee. Platform fees and applicable taxes are deducted before your payout is processed.
                Payouts are released <strong>{earnings?.payout_cycle_days} days</strong> after each completed inspection.
              </p>
            </div>

            {/* Transaction timeline */}
            <Card>
              <CardHeader><CardTitle className="text-base">Transaction History</CardTitle></CardHeader>
              <CardContent>
                {!earnings?.transactions?.length
                  ? (
                    <div className="text-center py-8 text-gray-400">
                      <IndianRupee className="h-10 w-10 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No transactions yet</p>
                    </div>
                  )
                  : <PayoutTimeline transactions={earnings.transactions} cycleLabel={cycleLabel} />
                }
              </CardContent>
            </Card>
          </div>
        )}

        {/* ══════════ AVAILABILITY TAB ══════════ */}
        {activeTab === 'availability' && (
          <div className="space-y-6">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calendar className="h-5 w-5 text-blue-600" />Set Your Availability</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-500">Set 2 availability slots so the system can auto-assign verification tasks to you.</p>
                {schedules.map((s, idx) => (
                  <div key={idx} className={`p-4 rounded-xl border-2 ${idx === 0 ? 'border-blue-200 bg-blue-50' : 'border-indigo-200 bg-indigo-50'}`}>
                    <p className="text-sm font-bold text-gray-700 mb-3">Slot {idx + 1}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs text-gray-500 font-medium block mb-1">Date *</label>
                        <input type="date" min={todayStr()} value={s.date}
                          onChange={e => setSchedules(prev => prev.map((x, i) => i === idx ? { ...x, date: e.target.value } : x))}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 font-medium block mb-1">From (min 10:00 AM)</label>
                        <input type="time" min="10:00" max="16:30" value={s.from_time}
                          onChange={e => setSchedules(prev => prev.map((x, i) => i === idx ? { ...x, from_time: e.target.value } : x))}
                          className="w-full border rounded-lg px-3 py-2 text-sm bg-white" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 font-medium block mb-1">To (max 6:00 PM)</label>
                        <input type="time" min="11:30" max="18:00" value={s.to_time}
                          onChange={e => setSchedules(prev => prev.map((x, i) => i === idx ? { ...x, to_time: e.target.value } : x))}
                          className="w-full border rounded-lg px-3 py-2 text-sm bg-white" />
                      </div>
                    </div>
                  </div>
                ))}
                <Button onClick={saveAvailability} disabled={savingAvail} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  {savingAvail ? 'Saving...' : 'Save Availability'}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">My Submitted Availability</CardTitle></CardHeader>
              <CardContent>
                {!availability.length
                  ? <p className="text-sm text-gray-400 text-center py-6">No availability set yet</p>
                  : (
                    <div className="space-y-3">
                      {availability.map((a, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200">
                          <div className="flex items-center gap-3">
                            <Calendar className="h-4 w-4 text-blue-500" />
                            <div>
                              <p className="text-sm font-semibold text-gray-800">{a.date}</p>
                              <p className="text-xs text-gray-500">{a.from_time} – {a.to_time}</p>
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${a.available ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {a.available ? 'Available' : 'Occupied'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )
                }
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default PartnerDashboard;